/**
 * Cocoon Modern — Fabric Configurator (PDP)
 *
 * Each Shopify product IS a specific configuration (Sofa / L-Sectional / etc).
 * The only variant axis is Fabric — values like "Cassis - Nature", "Velvet - Camel".
 *
 * This script handles: fabric selection → variant lookup → price update →
 * product image swap → URL sync → add-to-cart.
 */

class CocoonConfigurator extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.selectedFabric = null;
    this.currentVariant = null;
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-product-json]');
    if (dataEl) {
      try {
        this.product = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Configurator: Failed to parse product JSON', e);
        return;
      }
    }

    this.fabricSwatches = this.querySelectorAll('[data-fabric]');
    this.productImage = this.querySelector('[data-configurator-image]');
    this.priceDisplay = this.querySelector('[data-configurator-price]');
    this.fabricLabel = this.querySelector('[data-fabric-label]');
    this.deliveryBadge = this.querySelector('[data-delivery-badge]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.summaryFabric = this.querySelector('[data-summary-fabric]');
    this.swatchCta = this.querySelector('[data-swatch-cta]');

    // Map of fabric option_value (e.g. "Cassis - Nature") → swatch product variant URL
    this.swatchVariantMap = {};
    const mapEl = this.querySelector('[data-swatch-variant-map]');
    if (mapEl) {
      try {
        this.swatchVariantMap = JSON.parse(mapEl.textContent);
      } catch (e) {
        console.warn('Configurator: Failed to parse swatch variant map', e);
      }
    }

    this.selectInitialFabric();
    this.bindEvents();
  }

  selectInitialFabric() {
    // Prefer URL ?variant=<id>, otherwise first available fabric
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    let initial = null;

    if (variantId && this.product) {
      const v = this.product.variants.find(x => String(x.id) === variantId);
      if (v) {
        initial = Array.from(this.fabricSwatches).find(s => s.dataset.fabric === v.option1);
      }
    }

    if (!initial) {
      initial = this.fabricSwatches[0];
    }
    if (initial) this.selectFabric(initial);
  }

  bindEvents() {
    this.fabricSwatches.forEach(swatch => {
      swatch.addEventListener('click', () => this.selectFabric(swatch));
    });
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  selectFabric(swatch) {
    this.fabricSwatches.forEach(s => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    this.selectedFabric = swatch.dataset.fabric;
    const fabricType = swatch.dataset.fabricType;
    const eta = swatch.dataset.fabricEta;

    if (this.fabricLabel) {
      this.fabricLabel.textContent = swatch.dataset.fabricName || this.selectedFabric;
    }
    if (this.summaryFabric) {
      this.summaryFabric.textContent = swatch.dataset.fabricName || this.selectedFabric;
    }

    // "Free swatch" CTA — deep-link to the matching swatch product variant
    if (this.swatchCta && this.selectedFabric) {
      const swatchUrl = this.swatchVariantMap[this.selectedFabric];
      if (swatchUrl) this.swatchCta.setAttribute('href', swatchUrl);
    }

    // Delivery badge based on fabric tier
    if (this.deliveryBadge) {
      const etaText = eta || (
        fabricType === 'cassis' ? '3–4 weeks' :
        fabricType === 'belgian-linen' ? '6–8 weeks' :
        fabricType === 'velvet' ? '8–10 weeks' : ''
      );
      const tierLabel =
        fabricType === 'cassis' ? 'Cassis — Arrives in' :
        fabricType === 'belgian-linen' ? 'Belgian Linen — Arrives in' :
        fabricType === 'velvet' ? 'Velvet — Arrives in' : 'Arrives in';
      this.deliveryBadge.innerHTML =
        `<span class="cm-cfg__delivery-dot cm-cfg__delivery-dot--${fabricType || 'quick'}"></span>${tierLabel} ${etaText}`;
    }

    this.updateVariant();
  }

  updateVariant() {
    if (!this.product || !this.selectedFabric) return;

    // Single variant axis — option1 is Fabric
    const variant = this.product.variants.find(v => v.option1 === this.selectedFabric);
    if (!variant) return;

    this.currentVariant = variant;

    if (this.priceDisplay) {
      this.priceDisplay.textContent = this.formatMoney(variant.price);
    }
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (variant.featured_image && this.productImage) {
      const img = this.productImage.querySelector('img');
      if (img) {
        img.src = variant.featured_image.src;
        img.srcset = '';
      }
    }

    if (this.addToCartBtn) {
      const textEl = this.addToCartBtn.querySelector('.cm-cfg__atc-text');
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        if (textEl) textEl.textContent = 'Add to Cart — ' + this.formatMoney(variant.price);
      } else {
        this.addToCartBtn.disabled = true;
        if (textEl) textEl.textContent = 'Sold Out';
      }
    }

    // Keep URL in sync
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Notify other sections (sticky buy, etc.)
    document.dispatchEvent(new CustomEvent('cm-configurator:change', {
      detail: { fabric: this.selectedFabric, variant: variant }
    }));
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variant }
    }));
  }

  async addToCart() {
    if (!this.currentVariant || !this.currentVariant.available) return;

    const btn = this.addToCartBtn;
    const textEl = btn.querySelector('.cm-cfg__atc-text');
    const originalText = textEl ? textEl.textContent : '';

    btn.disabled = true;
    if (textEl) textEl.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.currentVariant.id, quantity: 1 })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      if (textEl) textEl.textContent = 'Added!';

      // CM cart drawer auto-opens via fetch interceptor, but also call directly
      if (window.cmCart && typeof window.cmCart.open === 'function') {
        window.cmCart.open();
      } else {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }

      setTimeout(() => {
        if (textEl) textEl.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    } catch (error) {
      if (textEl) textEl.textContent = 'Error — Try Again';
      btn.disabled = false;
      setTimeout(() => {
        if (textEl) textEl.textContent = originalText;
      }, 3000);
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
}

if (!customElements.get('cocoon-configurator')) {
  customElements.define('cocoon-configurator', CocoonConfigurator);
}
